// DndPropertiesSelector.tsx
import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Alert,
  AlertTitle,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Icon } from '@iconify/react';
import { Iconify } from 'src/components/iconify';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslate } from 'src/locales';
import {
  executeAdminActions,
  executeUserActions,
  getDndDraggableList,
  getBannerDetails,
} from 'src/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';
import { toast } from 'src/components/snackbar';
import { Scrollbar } from '../scrollbar';
import AuthUtils from 'src/utils/AuthUtils';
import { decodeJWT } from 'aws-amplify/auth';
import { useLocation } from 'react-router';
import { useBannerDetails } from 'src/context/BannerContext';
import { useHasPermission } from 'src/utils/helper';

interface Property {
  [key: string]: any;
  Priority?: number;
}

interface FieldColumn {
  order: number;
  label: string;
  data_column: string;
  data_type: string;
  is_hidden?: boolean;
}

interface UiField {
  name: string;
  label: string;
  heading: string;
  helper_text: string;
  required: boolean;
  initial_button_text: string;
  updated_button_text: string;
  initial_button_icon: string;
  updated_button_icon: string;
  draggable_list_sql_data: any;
  priority_list_sql_data: any;
  update_sql_data: any;
  fields: FieldColumn[];
}

interface UiDataSection {
  fields: UiField[];
  section_label?: string;
  hash?: string;
}

interface DndPropertiesSelectorProps {
  uiData: UiDataSection;
  disabled: boolean;
  fullApplicationIncomeCategory?: number;
  isAdmin: boolean;
  module_name?: string;
  sub_module?: string;
  refreshForm?: () => void;
  setIsCheckingForProperties?: (isChecking: boolean) => void;
  defaultData?: any;
  isOtherSectionsDirty?: boolean;
  onRevertChanges?: () => void;
  onSaveChanges?: () => void;
  clearPropertiesOnRescind?: number;
}

interface UserPayload {
  PersonId: string;
  UserId: string;
  RecordId: string;
}

export default function DndPropertiesSelector({
  uiData,
  disabled,
  fullApplicationIncomeCategory,
  isAdmin,
  module_name,
  sub_module,
  refreshForm,
  setIsCheckingForProperties,
  defaultData,
  isOtherSectionsDirty = false,
  onRevertChanges,
  onSaveChanges,
  clearPropertiesOnRescind,
}: DndPropertiesSelectorProps) {
  const field = uiData.fields[0];
  const name = field.name;
  const { control, setValue, formState } = useFormContext();
  const { isSubmitting } = formState;
  const formWatch = useWatch({ control, name });
  const [selectedProperties, setSelectedProperties] = useState<Property[]>([]);
  const [allProperties, setAllProperties] = useState<Property[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const { t } = useTranslate('form');
  const { bannerDetails, setBannerDetails } = useBannerDetails();
  const prevClearTriggerRef = useRef<number>(0);

  console.log(uiData?.fields[0], 'uiData');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ───────── permissions management ──────────────────────────────────────────────
  const hasPermission = useHasPermission();

  const location = useLocation();
  const paramsData: Record<string, any> = EncryptUtils.getDecryptedData(location.search, 'row');
  const getItemId = (item: Property) => item.FullApplicationPropertyId || item.id;

  useEffect(() => {
    const enriched = (formWatch || []).map((item: Property, index: number) => {
      const cloned = { ...item };
      delete cloned.FullApplicationPropertyId; // Remove it if it's new and was wrongly carried over
      return {
        ...cloned,
        Priority: index + 1,
      };
    });
    setSelectedProperties(enriched);
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, []);

  // Clear properties when rescind happens (only when clearPropertiesOnRescind increases)
  useEffect(() => {
    if (
      clearPropertiesOnRescind !== undefined &&
      clearPropertiesOnRescind > 0 &&
      clearPropertiesOnRescind > prevClearTriggerRef.current
    ) {
      prevClearTriggerRef.current = clearPropertiesOnRescind;
      setSelectedProperties([]);
      if (setIsCheckingForProperties) setIsCheckingForProperties(false);
      fetchPriorities();
    } else if (clearPropertiesOnRescind !== undefined) {
      // Update ref to track current value even if we don't clear
      prevClearTriggerRef.current = clearPropertiesOnRescind;
    }
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, [clearPropertiesOnRescind]);

  const fetchPriorities = async (CreatedRecordId?: any) => {
    try {
      const token = AuthUtils.getAuthToken();
      const decodedToken = decodeJWT(token);
      const tokenData = decodedToken as { payload?: { user?: UserPayload } };
      const req_module_name = module_name || 'applications';

      const recordId =
        bannerDetails?.FullApplicationRecordId || paramsData?.RecordId || CreatedRecordId || 0;

      // If record_id is 0 or null, clear properties immediately
      if (!recordId || recordId === 0) {
        setSelectedProperties([]);
        if (setIsCheckingForProperties) setIsCheckingForProperties(false);
        return;
      }

      const data: any = {
        sql_data: field.priority_list_sql_data,
        record_id: recordId,
        is_hidden: false,
      };
      const encrypted = EncryptUtils.encryptedData(data);
      const resp = await getDndDraggableList(req_module_name, { tk: encrypted });
      const enriched = (resp.data || []).map((item: Property, index: number) => ({
        ...item,
        Priority: index + 1,
      }));

      // Update flag based on database state (only when properties are saved)
      const hasProperties = enriched?.length > 0;
      if (setIsCheckingForProperties) setIsCheckingForProperties(hasProperties);
      setSelectedProperties(enriched);
    } catch (error) {
      console.error('Error fetching initial priorities:', error);
      // On error, assume no properties saved (safest state)
      setSelectedProperties([]);
      if (setIsCheckingForProperties) setIsCheckingForProperties(false);
    }
  };

  useEffect(() => {
    const fetchAvailableProperties = async () => {
      try {
        const req_module_name = module_name || 'applications';
        const incomeCode = fullApplicationIncomeCategory ?? paramsData?.IncomeCategoryCode;
        // console.log(incomeCode, 'incomeCode');
        const data: any = {
          sql_data: field.draggable_list_sql_data,
          stage_code: 100028,
          // income_category_code: fullApplicationIncomeCategory || paramsData?.IncomeCategoryCode,
          is_hidden: false,
        };
        if (incomeCode !== 0 && incomeCode != null) {
          data.income_category_code = incomeCode; // only when > 0
        }
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const resp = await getDndDraggableList(req_module_name, { tk: encryptRequestData });
        setAllProperties(resp.data || []);
      } catch (error) {
        console.error('Error fetching available properties:', error);
      }
    };

    fetchAvailableProperties();
    fetchPriorities();
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, [field.draggable_list_sql_data]);

  useEffect(() => {
    const formValue = formWatch || [];

    // Simple JSON stringify compare — or you can use lodash.isEqual if needed
    if (JSON.stringify(formValue) !== JSON.stringify(selectedProperties)) {
      setValue(name, selectedProperties, { shouldValidate: true });
    }
  }, [selectedProperties, formWatch, name, setValue]);

  const handleDrop = (event: any) => {
    if (isOtherSectionsDirty) {
      toast.warning('You need to save or revert your changes before reordering properties.');
      return;
    }
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = selectedProperties.findIndex((i) => getItemId(i) === active.id);
      const newIndex = selectedProperties.findIndex((i) => getItemId(i) === over?.id);
      const reordered = arrayMove(selectedProperties, oldIndex, newIndex).map((item, idx) => ({
        ...item,
        Priority: idx + 1,
      }));
      setSelectedProperties(reordered);
    }
  };

  const toggleSelect = (property: Property) => {
    const exists = selectedProperties.find((p) => p.PropertyId === property.PropertyId);
    if (exists) {
      const filtered = selectedProperties
        .filter((p) => p.PropertyId !== property.PropertyId)
        .map((item, idx) => ({
          ...item,
          Priority: idx + 1,
        }));
      setSelectedProperties(filtered);
    } else {
      const newItem = {
        ...property,
        id: property.id,
        // Only set FullApplicationPropertyId if it's already present (i.e., existing priority)
        ...(property.FullApplicationPropertyId && {
          FullApplicationPropertyId: property.FullApplicationPropertyId,
        }),
      };
      const updated = [...selectedProperties, newItem].map((item, idx) => ({
        ...item,
        Priority: idx + 1,
      }));
      setSelectedProperties(updated);
    }
  };

  const handleSave = async () => {
    try {
      if (!module_name) {
        // console.log(module_name, 'module_name is not present');
        return;
      }
      const token = AuthUtils.getAuthToken();
      const decodedToken = decodeJWT(token);
      const tokenData = decodedToken as { payload?: { user?: UserPayload } };
      // console.log(selectedProperties, paramsData, isAdmin, bannerDetails, 'preferences');
      // checks related to payload creation
      const req_case_id = isAdmin ? paramsData.CaseId : bannerDetails?.FullApplicationCaseId;
      const req_person_id = isAdmin ? paramsData.PersonId : bannerDetails?.PersonId;

      // if (!req_case_id || !req_person_id || !field?.update_sql_data) {
      //   // console.log(req_case_id, req_person_id, field?.update_sql_data, 'required data error');
      //   toast.error('Missing required data: PersonId, CaseId, or SQL update flag');
      //   return;
      // }

      const payload = {
        sql_data: field.update_sql_data || {},
        case_id: isAdmin
          ? paramsData.CaseId
          : defaultData
            ? defaultData?.CaseId
            : bannerDetails?.FullApplicationCaseId,
        person_id: isAdmin ? paramsData.PersonId : bannerDetails?.PersonId,
        FullApplicationPropertyList: selectedProperties as any,
      };

      // console.log('payload from save preferences', payload);

      let response;
      if (isAdmin) {
        if (!sub_module) return;
        response = await executeAdminActions(module_name, sub_module, {
          tk: EncryptUtils.encryptedData(payload),
        });
        toast.success('Changes successfully updated');
        fetchPriorities();
      } else {
        response = await executeUserActions(module_name, {
          tk: EncryptUtils.encryptedData(payload),
        });
        fetchPriorities();
        toast.success('Changes successfully updated');
      }

      if (response && refreshForm) {
        refreshForm();
        fetchPriorities(response?.data[0][0]?.CreatedRecordId);
      }
      setOpenDialog(false);
      // Refresh banner values after saving preferences
      const token_person_id = tokenData?.payload?.user?.PersonId;
      if (token_person_id) {
        try {
          const bannerPayload = { PersonId: token_person_id };
          const encryptedBannerPayload = EncryptUtils.encryptedData(bannerPayload);
          const bannerData = await getBannerDetails({ tk: encryptedBannerPayload });
          setBannerDetails(bannerData);
        } catch (bannerError) {
          console.error('Failed to refresh banner details:', bannerError);
        }
      }

      // Close modal after all operations complete
    } catch (error) {
      console.error('Failed to update preferences:', error);
      // Close modal even on error
      setOpenDialog(false);
    }
  };

  const handleOpenDialog = () => {
    if (isOtherSectionsDirty) {
      toast.warning(
        'You need to save or revert your changes in General Information, Members, or Requirements sections before editing property preferences.'
      );
      return;
    }
    setOpenDialog(true);
  };

  const isPropertiesDisabled = disabled || isOtherSectionsDirty;

  return (
    <Box>
      <Typography variant="h4">{t(uiData?.section_label || field.label)}</Typography>

      {isOtherSectionsDirty && hasPermission(uiData?.fields[0]) && (
        <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
          <AlertTitle>Unsaved Changes Detected</AlertTitle>
          You have unsaved changes in General Information, Members, or Requirements sections. Please
          save or revert these changes before editing property preferences.
          <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-start' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={onRevertChanges}
              disabled={isSubmitting}
            >
              Revert
            </Button>
            <LoadingButton
              size="small"
              variant="contained"
              onClick={onSaveChanges}
              loading={isSubmitting}
              loadingIndicator={<Iconify icon="eos-icons:loading" />}
            >
              Save
            </LoadingButton>
          </Box>
        </Alert>
      )}

      {selectedProperties.length === 0 ? (
        <Tooltip
          title={
            isOtherSectionsDirty
              ? 'You need to save or revert your changes before editing preferences.'
              : ''
          }
          disableHoverListener={!isOtherSectionsDirty}
        >
          <span>
            <Button
              onClick={handleOpenDialog}
              variant="outlined"
              sx={{ mt: 1 }}
              startIcon={<Icon icon={field.initial_button_icon} />}
              disabled={isPropertiesDisabled}
            >
              {field.initial_button_text}
            </Button>
          </span>
        </Tooltip>
      ) : (
        <>
          <Tooltip
            title={
              isOtherSectionsDirty
                ? 'You need to save or revert your changes before editing preferences.'
                : ''
            }
            disableHoverListener={!isOtherSectionsDirty}
          >
            <span>
              <Button
                onClick={handleOpenDialog}
                variant="outlined"
                sx={{ mt: 1 }}
                startIcon={<Icon icon={field.updated_button_icon} />}
                disabled={isPropertiesDisabled}
              >
                {field.updated_button_text}
              </Button>
            </span>
          </Tooltip>
          <Box mt={2}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Priority</TableCell>
                  {field.fields
                    .filter((col) => !col.is_hidden)
                    .map((col) => (
                      <TableCell key={col.data_column}>{col.label}</TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedProperties.map((p, i) => (
                  <TableRow key={getItemId(p)}>
                    <TableCell>{p.Priority}</TableCell>
                    {field.fields
                      .filter((col) => !col.is_hidden)
                      .map((col) => (
                        <TableCell key={col.data_column}>{p[col.data_column]}</TableCell>
                      ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}

      <Dialog
        open={openDialog}
        onClose={() => {
          fetchPriorities();
          setOpenDialog(false);
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{field.label}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={3}>
            <Box flex={1}>
              <Typography variant="subtitle2">Available Properties</Typography>
              <Scrollbar sx={{ maxHeight: isMobile ? "50vh" : 500 }}>
                {allProperties.map((property) => (
                  <Card
                    key={property.id}
                    variant="outlined"
                    sx={{ mb: 2, width: isMobile ? '100%' : 'auto' }}
                  >
                    <CardContent>
                      {field.fields
                        .filter((col) => !col.is_hidden)
                        .map((col) => (
                          <Typography variant="body2" key={col.data_column}>
                            <strong>{col.label}:</strong> {property[col.data_column]}
                          </Typography>
                        ))}
                      <Button
                        onClick={() => toggleSelect(property)}
                        fullWidth
                        variant={
                          selectedProperties.some((p) => p.PropertyId === property.PropertyId)
                            ? 'contained'
                            : 'outlined'
                        }
                        sx={{ mt: 1 }}
                      >
                        {selectedProperties.some((p) => p.PropertyId === property.PropertyId)
                          ? 'Remove'
                          : 'Add'}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </Scrollbar>
            </Box>

            <Box flex={1}>
              <Typography variant="subtitle2">{field.helper_text}</Typography>
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDrop}>
                <SortableContext
                  items={selectedProperties.map(getItemId)}
                  strategy={verticalListSortingStrategy}
                >
                  {selectedProperties.map((property, index) => (
                    <SortableItem
                      key={getItemId(property)}
                      property={property}
                      index={index}
                      labelKey={field.fields[1].data_column}
                      toggleSelect={toggleSelect}
                      disabled={isOtherSectionsDirty}
                      isMobile={isMobile}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              fetchPriorities();
              setOpenDialog(false);
            }}
          >
            Close
          </Button>
          <Button onClick={handleSave} variant="contained">
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SortableItem({
  property,
  index,
  labelKey,
  toggleSelect,
  disabled = false,
  isMobile,
}: {
  property: Property;
  index: number;
  labelKey: string;
  toggleSelect: any;
  disabled?: boolean;
  isMobile: any;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: property.FullApplicationPropertyId || property.id,
    disabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: isMobile ? '10px' : '0px',
    padding: '12px',
    border: '1px solid #ddd',
    marginBottom: '12px',
    borderRadius: 6,
    backgroundColor: 'background.paper',
  };

  return (
    <Box ref={setNodeRef} style={style} {...attributes}>
      <Box width={isMobile ? '100%' : '10%'}>{index + 1}</Box>
      <Box width={isMobile ? '100%' : '75%'}>{property[labelKey]}</Box>
      <Box
        width={isMobile ? '100%' : '15%'}
        display="flex"
        justifyContent={isMobile ? 'space-between' : 'end'}
        alignItems="center"
      >
        <span
          {...(disabled ? {} : listeners)}
          style={{ cursor: disabled ? 'not-allowed' : 'grab', opacity: disabled ? 0.5 : 1 }}
        >
          <IconButton sx={{ cursor: disabled ? 'not-allowed' : 'grab' }} disabled={disabled}>
            <Icon icon="mdi:drag" width="20" height="20" />
          </IconButton>
        </span>
        <IconButton onClick={() => toggleSelect(property)}>
          <Icon
            icon="mdi:cross-circle-outline"
            color="#ff5630"
            width="20"
            height="20"
            cursor="pointer"
          />
        </IconButton>
      </Box>
    </Box>
  );
}
